import { useEffect, useState } from 'react';
import logo from '../images/Logo.png';
import '../styles/Nav.css';
import { forceScrollWindowToTop, queueMicrotaskSafe, smoothScrollWindowToTop } from '../utils/scrollWindow';

const navData = {
  swe: [
    { label: 'Hem', page: 'home' },
    { label: 'Tjänster', href: '#services' },
    { label: 'Om oss', page: 'about' },
    { label: 'Kontakt', page: 'contact' }
  ],
  eng: [
    { label: 'Home', page: 'home' },
    { label: 'Services', href: '#services' },
    { label: 'About', page: 'about' },
    { label: 'Contact', page: 'contact' }
  ]
};

function NavSwe({ onClick, active }) {
  return (
    <button className={`lang-btn ${active ? 'active' : ''}`} onClick={() => onClick('swe')}>
      <span role="img" aria-label="Swedish">🇸🇪</span> Svenska
    </button>
  );
}

function NavEng({ onClick, active }) {
  return (
    <button className={`lang-btn ${active ? 'active' : ''}`} onClick={() => onClick('eng')}>
      <span role="img" aria-label="English">🇬🇧</span> English
    </button>
  );
}

function Nav({ lang, setLang, page, navigateTo }) {
  const [open, setOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMenu = () => {
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY;
      setIsScrolled(currentY > 20);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = navData[lang];
  const goHome = () => {
    if (page === 'home') {
      smoothScrollWindowToTop();
      return;
    }

    navigateTo('home');
    queueMicrotaskSafe(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => smoothScrollWindowToTop());
      });
    });
  };

  const runClick = (label, fn) => {
    try {
      // eslint-disable-next-line no-console
      console.debug(`[Nav click] ${label}`);
      fn();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[Nav click] ${label} failed`, err);
      // Fallback so navigation still "does something" even if smooth scroll path breaks.
      forceScrollWindowToTop();
    }
  };

  return (
    <header className={`nav-shell ${isScrolled ? 'scrolled' : ''} ${open ? 'menu-open' : ''}`}>
      <div className="nav-wrap">
        <div className="nav-brand">
          <button
            type="button"
            className="nav-brand-btn"
            onClick={() => {
              runClick('brand/home', () => {
                goHome();
                setOpen(false);
              });
            }}
            aria-label={lang === 'swe' ? 'Gå till startsidan' : 'Go to home'}
          >
            <img src={logo} alt="Execora logo" className="nav-logo" />
            <h4>Execora Consulting</h4>
          </button>
        </div>

        <div className="nav-lang-desktop">
          <NavSwe onClick={setLang} active={lang === 'swe'} />
          <NavEng onClick={setLang} active={lang === 'eng'} />
        </div>

        <button className="nav-toggle" onClick={toggleMenu} aria-expanded={open} aria-label="Toggle menu">
          <span className="hamburger"></span>
        </button>

        <nav className={`nav-links ${open ? 'open' : ''}`}>
          {links.map((item) => {
            if (item.page) {
              return (
                <button
                  key={item.label}
                  type="button"
                  className={`nav-link nav-button ${page === item.page ? 'active' : ''}`}
                  onClick={() => {
                    runClick(`nav/${item.page}`, () => {
                      if (item.page === 'home') {
                        goHome();
                      } else {
                        navigateTo(item.page);
                      }
                      setOpen(false);
                    });
                  }}
                >
                  {item.label}
                </button>
              );
            }

            return (
              <a
                key={item.label}
                href={item.href}
                className="nav-link"
                onClick={(event) => {
                  event.preventDefault();
                  navigateTo(item.href);
                  setOpen(false);
                }}
              >
                {item.label}
              </a>
            );
          })}
        </nav>
      </div>

      <div className="lang-switcher-wrap">
        <div className="lang-switcher">
          <NavSwe onClick={setLang} active={lang === 'swe'} />
          <NavEng onClick={setLang} active={lang === 'eng'} />
        </div>
      </div>
    </header>
  );
}

export default Nav;